import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  TextareaAutosize,
  Typography,
  Box,
  Autocomplete,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"; // <-- added
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { utcToLocal } from "../../Utils/timeConversions";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../Context/AuthProvider";
import { useTradeBlotterOrderStore } from "../../Store/tradeBlotterOrderStore";
import { IntegerInput, FloatInput } from "../OrderEntry/NumericInputs";
import { inputStyle } from "../OrderEntry/styles";
import { fieldOptions, updateVenues } from "../OrderEntry/optionsHelper";
import { PROTOCOL_TYPES, USER_ROLES } from "../../Constants/AppConstants";
// import { fetchCounterparties } from "../../http/api";
import AddCounterpartyDialog from "./AddCounterpartyDialog";
import useCounterparties from "./useCounterparties";
import { tokens, withAlpha } from "../../Common/designTokens"; // added

const TradeEntryDetails = ({ clearModalError, onPriceBlur, calcLoading, onExecutionDateTimeAccept, lockedFields = [] }) => {
  const {
    tradeEntryData,
    setTradeEntryData,
    clearFieldError,
    pmUsers,
    fetchPMUsers,
    fetchPMAccounts,
    setSelectedPM,
    accountIdentifiers,
    fieldErrors,
    isEntryLoading,
    cusipError,
    validateCusip,
    fetchAccounts, // <-- added
    counterpartyClearCounter, // <- listen for clear events
  } = useTradeBlotterOrderStore();
  const { token: jwtToken } = useAuth();
  const decodedToken = jwtDecode(jwtToken);
  const tokenRole = decodedToken.role;

  // Load counterparties with pagination (default: size=10000, sort by name asc)
  const {
    options: counterpartyOptions,
    loading: counterpartyLoading,
    addOption: addCounterpartyOption,
  } = useCounterparties(jwtToken);

  // NEW: dialog open state (UI-only)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // NEW: control Autocomplete popup programmatically
  const [counterpartyOpen, setCounterpartyOpen] = useState(false);
  const [counterpartyValidationError, setCounterpartyValidationError] = useState(false);

  useEffect(() => {
    if (tokenRole === USER_ROLES.TRADER) {
      fetchPMUsers(jwtToken);
    }
  }, [tokenRole, fetchPMUsers, jwtToken]);

  // NEW: fetch accounts for PM role so dropdown is populated
  useEffect(() => {
    fetchAccounts(jwtToken);

  }, [tokenRole, fetchAccounts, jwtToken]);

  useEffect(() => {
    updateVenues(jwtToken);
  }, [jwtToken]);

  useEffect(() => {
    const validateCusipWithDelay = async () => {
      const typed = tradeEntryData.cusip;
      const storedRef = tradeEntryData.cusipRef;
      if (!typed) return;
      // If we already have a stored canonical cusip matching typed value, normalize and skip API
      if (storedRef && String(storedRef).toLowerCase() === String(typed).toLowerCase()) {
        setTradeEntryData({ cusip: storedRef });
        return;
      }
      if (typed && typed.length >= 9) {
        validateCusip(jwtToken);
      }
    };

    const debounceTimeout = setTimeout(() => {
      if (tradeEntryData.cusip) validateCusipWithDelay();
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [tradeEntryData.cusip, jwtToken, validateCusip, tradeEntryData.cusipRef, setTradeEntryData]);

  // const handlePMChange = (event) => {
  //   const pmId = event.target.value;
  //   const selectedPM = pmUsers.find((pm) => pm.userId === pmId);
  //   setSelectedPM(selectedPM);
  //   setTradeEntryData({
  //     ...tradeEntryData,
  //     pmId: pmId,
  //     pm: selectedPM ? selectedPM.username : "",
  //     account: "",
  //   });

  //   if (pmId) {
  //     clearFieldError("pmId");
  //     clearModalError && clearModalError("pmId");
  //   }

  //   fetchPMAccounts(jwtToken, pmId);
  // };

  const datePickerStyles = {
    ...inputStyle,
    "& .MuiFilledInput-root": {
      ...inputStyle["& .MuiFilledInput-root"],
      paddingTop: "10px",
      borderRadius: "8px",
      backgroundColor: tokens.colors.white,
    },
    "& .MuiInputLabel-root": {
      ...inputStyle["& .MuiInputLabel-root"],
      color: tokens.colors.fontColour85,
      "&.Mui-focused": {
        color: tokens.palette.primary,
        transform: "translate(12px, 7px) scale(0.75)",
      },
      "&.MuiInputLabel-shrink": {
        transform: "translate(12px, 7px) scale(0.75)",
      },
    },
    "& .MuiInputLabel-root.Mui-error": { color: "error.main" },
    "& .MuiFilledInput-root.Mui-error .MuiFilledInput-input": { color: "error.main" },
    "& .MuiFilledInput-input": {
      color: tokens.colors.fontColour,
      padding: "14px !important",
    },
  };

  const commentTextAreaStyle = {
    width: "100%",
    minHeight: "32px",
    padding: "8px",
    boxSizing: "border-box",
    border: `1px solid ${withAlpha(tokens.colors.fontColour, 0.2)}`,
    borderRadius: "8px",
    backgroundColor: tokens.colors.defaultGridRowColor,
    color: tokens.colors.fontColour,
    resize: "vertical",
    fontFamily: "inherit",
    fontSize: "inherit",
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
    overflowX: "hidden",
    lineHeight: "32px",
    "&:disabled": {
      opacity: 0.95,
      backgroundColor: tokens.colors.defaultGridRowColor,
      cursor: "not-allowed",
      color: tokens.colors.disabledFontColour,
    },
    "&:focus": {
      outline: "none",
      border: `1px solid ${withAlpha(tokens.palette.primary, 0.25)}`,
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let isValid = true;

    if (name === "qty" && value > 10000000) isValid = false;
    if (name === "price" && value > 150) isValid = false;
    if (name === "tradeQuantity" && value > 10000000) isValid = false;
    if (name === "tradePrice" && value > 150) isValid = false;
    //TODO: validations for principalAmount, accruedInterest, estimatedTotal
    if (isValid) {
      let updatedData = { ...tradeEntryData, [name]: value };

      // If user cleared the CUSIP field, also clear cusipRef immediately
      if (name === "cusip" && (!value || String(value).trim() === "")) {
        updatedData.cusipRef = '';
      }

      if (name === "tradeQuantity") {
        updatedData.qty = value;
      } else if (name === "qty") {
        updatedData.tradeQuantity = value;
      }

      if (name === "tradePrice") {
        updatedData.price = value;
      } else if (name === "price") {
        updatedData.tradePrice = value;
      }

      if (name === "counterPartyId" || name === "counterparty") {
        updatedData.counterPartyId = value;
      }

      // NEW: Recalculate estimatedTotal if user edits principal or accrued
      if (name === "principalAmount" || name === "accruedInterest") {
        const principal = parseFloat(
          name === "principalAmount" ? value : updatedData.principalAmount
        );
        const accrued = parseFloat(
          name === "accruedInterest" ? value : updatedData.accruedInterest
        );
        if (Number.isFinite(principal) && Number.isFinite(accrued)) {
          updatedData.estimatedTotal = (principal + accrued).toFixed(2);
        } else {
          // Clear if incomplete / invalid
          updatedData.estimatedTotal = "";
        }
      }

      setTradeEntryData(updatedData);

      if (value !== undefined && value !== "" && value !== null) {
        clearFieldError(name);
        clearModalError && clearModalError(name);
      }

      if (name === "cusip" && value.length >= 9) {
        clearFieldError("cusip");
        clearModalError && clearModalError("cusip");
      }
    }
  };

  const filterVenues = (venues) => {
    let filteredVenues = venues.filter(option => {
      if (tradeEntryData.protocol === PROTOCOL_TYPES.RFQ) {
        const optionLower = option.toLowerCase();
        return !(
          /ecn[12]/.test(optionLower) ||
          optionLower.includes('custodian') ||
          optionLower.includes('schwab') ||
          /trading\s+desk/i.test(option)
        );
      }
      return true;
    });

    if (tradeEntryData.venue &&
      typeof tradeEntryData.venue === 'string' &&
      !filteredVenues.includes(tradeEntryData.venue)) {
      filteredVenues.push(tradeEntryData.venue);
    }

    return filteredVenues;
  };

  const isLocked = (field) => lockedFields.includes(field);

  const renderAccountField = () => {
    const baseProps = {
      fullWidth: true,
      variant: "filled",
      sx: inputStyle,
      InputProps: {
        disableUnderline: true,
      },
    };

    const accountProps = {
      ...baseProps,
      error: fieldErrors?.account,
      InputLabelProps: {
        sx: {
          // make the Account label white like other labels
          color: fieldErrors?.account ? 'error.main' : tokens.colors.fontColour,
          '&.Mui-focused': {
            color: fieldErrors?.account ? 'error.main' : tokens.palette.primary,
          },
        },
      },
    };

    return (
      <Grid item xs={3}>
        <TextField
          {...accountProps}
          select
          value={tradeEntryData.account || ""}
          onChange={handleChange}
          name="account"
          label="Account"
          disabled={isLocked("account")}
          InputLabelProps={accountProps.InputLabelProps}
        >
          {accountIdentifiers.map((identifier) => (
            <MenuItem key={identifier} value={identifier}>
              {identifier}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    );
  };

  // --- new: local execution datetime state to track picker changes while open ---
  const [localExecutionDateTime, setLocalExecutionDateTime] = useState(
    utcToLocal(tradeEntryData.executionDateTime)
  );

  // keep local state in sync when store value changes (e.g., prefill, resets)
  useEffect(() => {
    setLocalExecutionDateTime(utcToLocal(tradeEntryData.executionDateTime));
  }, [tradeEntryData.executionDateTime]);

  // Called when user changes the picker value (typing/selecting) — only update local state
  const handleExecutionDateChange = (v) => {
    setLocalExecutionDateTime(v ? v : null);
  };

  // Called only when user confirms (clicks OK) — compare and update store if different, then notify parent
  const handleExecutionDateAccept = (v) => {
    const formatted = v ? v.format("YYYY-MM-DDTHH:mm") : "";
    const storeVal = tradeEntryData.executionDateTime || "";
    if (formatted !== storeVal) {
      setTradeEntryData({ ...tradeEntryData, executionDateTime: formatted });
      clearFieldError && clearFieldError("executionDateTime");
      clearModalError && clearModalError("executionDateTime");
    }
    // Notify parent (will perform bond calculations etc.)
    onExecutionDateTimeAccept && onExecutionDateTimeAccept(v);
  };
  // --- end new ---

  return (
    <Grid container spacing={2}>
      {/* Core trade details */}
      <Grid item xs={3}>
        <TextField
          variant="filled"
          label="Identifier"
          fullWidth
          name="cusip"
          disabled={isLocked("cusip")}
          value={tradeEntryData.cusip}
          onChange={handleChange}
          sx={inputStyle}
          placeholder="Enter CUSIP (e.g. 914302ER1)"
          error={fieldErrors?.cusip || !!cusipError}
          helperText={cusipError}
          InputProps={{
            endAdornment: isEntryLoading && <CircularProgress size={20} />,
            disableUnderline: true,
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="side"
          value={tradeEntryData.side}
          onChange={handleChange}
          variant="filled"
          label="Side"
          disabled={isLocked("side")}
          error={fieldErrors?.side}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          {fieldOptions.side.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {renderAccountField()}
      <Grid item xs={3}>
        <IntegerInput
          name="tradeQuantity"
          value={tradeEntryData.tradeQuantity}
          onChange={handleChange}
          placeholder="Enter trade quantity"
          label="Trade Quantity"
          error={fieldErrors?.tradeQuantity}
          maxValue={10000000}
          sx={inputStyle}
        />
      </Grid>
      <Grid item xs={3}>
        <FloatInput
          name="tradePrice"
          value={tradeEntryData.tradePrice || ""}
          onChange={handleChange}
          placeholder="Enter trade price"
          label="Trade Price"
          error={fieldErrors?.tradePrice}
          maxValue={150}
          sx={inputStyle}
          onBlur={onPriceBlur}
          InputProps={{
            endAdornment: calcLoading && <CircularProgress size={20} />,
          }}
        />
      </Grid>

      {/* Execution Date Time: placed immediately after Trade Price */}
      <Grid item xs={3}>
        <DateTimePicker
          label="Execution Date Time "
          value={localExecutionDateTime}
          onChange={handleExecutionDateChange}
          onAccept={handleExecutionDateAccept}
          // maxDateTime={dayjs()}  // removed to avoid disabling times on current date
          shouldDisableDate={(d) => d && d.isAfter(dayjs(), "day")} // disable only future dates
          openTo="day"
          slotProps={{
            actionBar: { actions: ["cancel", "accept"] },
            textField: {
              variant: "filled",
              fullWidth: true,
              error: fieldErrors?.executionDateTime,
              sx: {
                ...datePickerStyles,
                "& .MuiFilledInput-input": {
                  ...datePickerStyles["& .MuiFilledInput-input"],
                  cursor: "pointer",
                },
              },
              InputProps: {
                disableUnderline: true,
                readOnly: true,
              },
              onClick: (e) => {
                // Ensure clicking anywhere on the field opens the picker
                e.currentTarget.querySelector('button')?.click();
              },
            },
          }}
        />
      </Grid>

      {/* Settlement Date DatePicker (moved after execution) */}
      <Grid item xs={3}>
        <DatePicker
          label="Settlement Date"
          value={tradeEntryData.settlementDate ? dayjs(tradeEntryData.settlementDate) : null}
          onChange={(v) => {
            handleChange({
              target: {
                name: "settlementDate",
                value: v ? v.format("YYYY-MM-DD") : "",
              },
            });
          }}
          slotProps={{
            textField: {
              variant: "filled",
              fullWidth: true,
              error: fieldErrors?.settlementDate,
              sx: datePickerStyles,
              InputProps: { disableUnderline: true },
            },
          }}
        />
      </Grid>

      {/* Counterparty Autocomplete (moved after settlement) */}
      <Grid item xs={3}>
        <Autocomplete
          open={counterpartyOpen}
          onOpen={() => setCounterpartyOpen(true)}
          onClose={() => setCounterpartyOpen(false)}
          value={
            counterpartyOptions.find(
              (o) => String(o.id) === String(tradeEntryData.counterPartyId)
            ) || null
          }
          inputValue={tradeEntryData.counterparty || ""}
          isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
          onChange={(event, newOption) => {
            if (newOption) {
              setTradeEntryData({
                ...tradeEntryData,
                counterPartyId: String(newOption.id),
                counterparty: newOption.name,
              });
              clearFieldError("counterPartyId");
              clearModalError && clearModalError("counterPartyId");
            } else {
              setTradeEntryData({
                ...tradeEntryData,
                counterPartyId: "",
                counterparty: "",
              });
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            const nextText = newInputValue || "";
            const next = { ...tradeEntryData, counterparty: nextText };
            if (reason === "input") {
              next.counterPartyId = "";
            }
            setTradeEntryData(next);
          }}
          options={counterpartyOptions}
          getOptionLabel={(option) => option?.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Counterparty"
              variant="filled"
              error={fieldErrors?.counterPartyId || counterpartyValidationError}
              sx={inputStyle}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    {/* Reduced width for icon container and tighter icon spacing so input gets max room */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", ml: 1, width: 36, minWidth: 36, justifyContent: "flex-end" }}>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => { e.preventDefault(); }}
                        onClick={() => setCounterpartyOpen(true)}
                        sx={{ color: tokens.colors.fontColour85, p: "4px", minWidth: 0, width: 22, height: 22 }}
                        aria-hidden
                      >
                        <ArrowDropDownIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setIsAddDialogOpen(true)}
                        sx={{ color: tokens.colors.fontColour, p: "4px", minWidth: 0, width: 22, height: 22 }}
                        aria-label="add counterparty"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                ),
              }}
              InputLabelProps={{
                sx: {
                  color:
                    fieldErrors?.counterPartyId || counterpartyValidationError
                      ? "error.main"
                      : tokens.colors.fontColour85,
                  "&.Mui-focused": {
                    color:
                      fieldErrors?.counterPartyId || counterpartyValidationError
                        ? "error.main"
                        : tokens.palette.primary,
                  },
                },
              }}
            />
          )}
          freeSolo
          disableClearable
          disabled={isLocked("counterPartyId")}
          sx={{
            ...inputStyle,
            // allow the filled input and inner input to use remaining space and keep minimal right padding
            "& .MuiFilledInput-root": {
              ...inputStyle["& .MuiFilledInput-root"],
              paddingLeft: "0px",
              paddingRight: "6px",
              // ensure the input element can shrink/grow correctly
              display: "flex",
              alignItems: "center",
            },
            "& .MuiFilledInput-input": {
              // give the inner input element max room and small right padding
              paddingRight: "6px",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            // reduce horizontal gap impact from endAdornment wrapper
            "& .MuiAutocomplete-endAdornment": {
              right: 6,
            },
          }}
        />
        <AddCounterpartyDialog
          open={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onAdd={(created) => {
            if (!created) return;
            addCounterpartyOption(created);
            setIsAddDialogOpen(false);
          }}
        />
      </Grid>

      {/* Financial & scheduling (merged) */}
      <Grid item xs={3}>
        <FloatInput
          name="principalAmount"
          value={tradeEntryData.principalAmount || ""}
          onChange={handleChange}
          placeholder="Enter principal amount"
          label="Principal Amount"
          error={fieldErrors?.principalAmount}
          sx={inputStyle}
          decimalScale={2}
        />
      </Grid>
      <Grid item xs={3}>
        <FloatInput
          name="accruedInterest"
          value={tradeEntryData.accruedInterest || ""}
          onChange={handleChange}
          placeholder="Enter accrued interest"
          label="Accrued Interest"
          error={fieldErrors?.accruedInterest}
          sx={inputStyle}
          decimalScale={2}
          InputProps={{
            endAdornment: calcLoading && <CircularProgress size={20} />,
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <FloatInput
          name="estimatedTotal"
          value={tradeEntryData.estimatedTotal || ""}
          placeholder="Auto-calculated"
          label="Estimated Total"
          error={fieldErrors?.estimatedTotal}
          sx={inputStyle}
          decimalScale={2}
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ position: "relative", width: "100%" }}>
          <TextareaAutosize
            name="comments"
            value={tradeEntryData.comments || ""}
            onChange={(e) => {
              if (e.target.value.length <= 200) {
                setTradeEntryData({ ...tradeEntryData, comments: e.target.value });
              }
            }}
            placeholder="Add Comments"
            minRows={1}
            maxRows={5}
            style={commentTextAreaStyle}
          />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 4,
              right: 8,
              color:
                (tradeEntryData.comments?.length || 0) > 200
                  ? "error.main"
                  : tokens.colors.fontColour85,
              fontSize: "0.75rem",
              pointerEvents: "none",
            }}
          >
            {tradeEntryData.comments?.length || 0}/200
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TradeEntryDetails;